/**
 * Renovation Photos Block
 * Displays the 10 most recent photos uploaded to renovation projects
 * Shows RENO code, project title, and building ID for each photo
 */

import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router-dom";
import { Image, Images, X, ArrowLeft, ArrowRight, Plus, ArrowSquareOut, FloppyDisk, SpinnerGap, Info } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { DashboardBlock, DashboardBlockHeader, DashboardBlockContent } from "../layout/DashboardBlock";
import { BlockLoadingState, BlockEmptyState } from "../states/BlockStates";
import { UploadType } from "shared/components/uploadFileModal/uploadFileModal";
import { format, isThisWeek } from "date-fns";
import { nl } from "date-fns/locale";

interface RenovationPhoto {
  id: number;
  imageUrl?: string;
  sharePointFileUrl?: string;
  label?: string | string[];
  documentDate?: string;
  createdAt?: string;
  projectId: number;
  projectTitle?: string;
  buildingId: string;
  summary?: string;
}

/**
 * Renovation Photos Block
 * Shows latest renovation project photos with project and building context
 */
export const RenovationPhotosBlockNew = () => {
  const [photos, setPhotos] = useState<RenovationPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<RenovationPhoto | null>(null);
  const [editedSummary, setEditedSummary] = useState<string>("");
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const { session } = useSession();
  const navigate = useNavigate();

  const handlePhotoUpload = () => {
    navigate("/upload-files", {
      state: {
        uploadType: UploadType.PICTURES,
      },
    });
  };

  useEffect(() => {
    const fetchRecentPhotos = async () => {
      if (!session) return;

      setIsLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/renovation-photos/recent?limit=30`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const data = await response.json();
          setPhotos(data);
        } else {
          console.error("Error fetching renovation photos:", response.status);
          setPhotos([]);
        }
      } catch (error) {
        console.error("Error fetching renovation photos:", error);
        setPhotos([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentPhotos();
  }, [session]);

  const getPhotoUrl = (photo: RenovationPhoto) => {
    if (photo.imageUrl) return photo.imageUrl;
    if (photo.sharePointFileUrl) return photo.sharePointFileUrl;
    return "/placeholder-image.jpg";
  };

  const getPhotoDate = (photo: RenovationPhoto) => {
    return new Date(photo.documentDate || photo.createdAt || 0);
  };

  const formatPhotoDate = (photo: RenovationPhoto) => {
    const date = getPhotoDate(photo);
    return format(date, "d MMM yyyy", { locale: nl });
  };

  const formatPhotoDateShort = (photo: RenovationPhoto) => {
    const date = getPhotoDate(photo);
    return format(date, "d MMM", { locale: nl });
  };

  const isPhotoFromCurrentWeek = (photo: RenovationPhoto) => {
    const date = getPhotoDate(photo);
    return isThisWeek(date, { weekStartsOn: 1 }); // Monday as start of week
  };

  const handlePhotoClick = (photo: RenovationPhoto) => {
    setSelectedPhoto(photo);
    setEditedSummary(photo.summary || "");
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
    setEditedSummary("");
  };

  const handleSaveSummary = async () => {
    if (!selectedPhoto || !session) return;

    setIsSavingSummary(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/update-file-log/${selectedPhoto.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ summary: editedSummary }),
        },
        session
      );

      if (response.ok) {
        // Update local state
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === selectedPhoto.id ? { ...p, summary: editedSummary } : p
          )
        );
        setSelectedPhoto((prev) =>
          prev ? { ...prev, summary: editedSummary } : null
        );
        toast.success("Samenvatting opgeslagen");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Opslaan mislukt");
      }
    } catch (error) {
      console.error("Error saving summary:", error);
      toast.error(error instanceof Error ? error.message : "Kon samenvatting niet opslaan");
    } finally {
      setIsSavingSummary(false);
    }
  };

  const handlePrevPhoto = () => {
    if (!selectedPhoto) return;
    const currentIndex = photos.findIndex((p) => p.id === selectedPhoto.id);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
    const prevPhoto = photos[prevIndex];
    setSelectedPhoto(prevPhoto);
    setEditedSummary(prevPhoto.summary || "");
  };

  const handleNextPhoto = () => {
    if (!selectedPhoto) return;
    const currentIndex = photos.findIndex((p) => p.id === selectedPhoto.id);
    const nextIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
    const nextPhoto = photos[nextIndex];
    setSelectedPhoto(nextPhoto);
    setEditedSummary(nextPhoto.summary || "");
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedPhoto) return;
      if (e.key === "Escape") handleCloseModal();
      if (e.key === "ArrowLeft") handlePrevPhoto();
      if (e.key === "ArrowRight") handleNextPhoto();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedPhoto]);

  return (
    <>
      <DashboardBlock>
        <DashboardBlockHeader
          icon={<Images weight="duotone" size={20} />}
          title="Recente renovatie foto's"
          action={
            <button
              onClick={handlePhotoUpload}
              className="flex items-center gap-1 text-sm font-medium text-moss-primary hover:text-moss-primary/80 transition-colors"
            >
              <Plus size={16} weight="bold" />
              Foto uploaden
            </button>
          }
        />

        {isLoading ? (
          <DashboardBlockContent padding="none">
            <BlockLoadingState message="Foto's aan het ophalen..." />
          </DashboardBlockContent>
        ) : photos.length > 0 ? (
          <DashboardBlockContent padding="none" className="relative overflow-hidden">
            <div className="p-3">
              {/* Photo Grid - 6 columns */}
              <div className="grid grid-cols-6 gap-2">
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    onClick={() => handlePhotoClick(photo)}
                    className="group cursor-pointer overflow-hidden rounded-md bg-neutral-100 transition-all hover:ring-2 hover:ring-moss-primary"
                  >
                    {/* Image */}
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={getPhotoUrl(photo)}
                        alt={`RENO-${photo.projectId}`}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        loading="lazy"
                      />
                      {/* Summary indicator */}
                      {photo.summary && (
                        <div className="absolute top-1 right-1 p-0.5 rounded-full bg-moss-primary/90">
                          <Info size={10} weight="bold" className="text-white" />
                        </div>
                      )}
                    </div>
                    {/* Info below image */}
                    <div className="bg-neutral-primary px-1.5 py-1">
                      <div className="flex items-center justify-between gap-1">
                        <div className="text-[9px] font-semibold text-white truncate">
                          RENO-{photo.projectId}
                        </div>
                        <div className={`text-[8px] font-medium whitespace-nowrap ${
                          isPhotoFromCurrentWeek(photo)
                            ? "text-emerald-400"
                            : "text-white/50"
                        }`}>
                          {formatPhotoDateShort(photo)}
                        </div>
                      </div>
                      {photo.projectTitle && (
                        <div className="text-[8px] text-white/80 truncate">
                          {photo.projectTitle}
                        </div>
                      )}
                      <div className="text-[8px] text-white/60 truncate">
                        {photo.buildingId}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Background decoration */}
            <div className="absolute bottom-0 right-0 z-0 translate-x-1/4 translate-y-1/4 text-moss-primary/5 pointer-events-none">
              <Images weight="duotone" size={100} />
            </div>
          </DashboardBlockContent>
        ) : (
          <DashboardBlockContent padding="none">
            <BlockEmptyState
              icon={<Image weight="duotone" size={48} />}
              title="Geen foto's gevonden"
              description="Er zijn nog geen foto's geupload bij renovatieprojecten"
            />
          </DashboardBlockContent>
        )}
      </DashboardBlock>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          {/* Close button */}
          <button
            onClick={handleCloseModal}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X size={24} weight="bold" className="text-white" />
          </button>

          {/* Navigation buttons */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrevPhoto();
            }}
            className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeft size={24} weight="bold" className="text-white" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNextPhoto();
            }}
            className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowRight size={24} weight="bold" className="text-white" />
          </button>

          {/* Image and info */}
          <div
            className="max-w-4xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getPhotoUrl(selectedPhoto)}
              alt={`RENO-${selectedPhoto.projectId}`}
              className="max-h-[75vh] object-contain rounded-t-lg"
            />
            <div className="bg-neutral-primary rounded-b-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold text-white">
                    RENO-{selectedPhoto.projectId}
                    {selectedPhoto.projectTitle && (
                      <span className="font-normal text-white/80 ml-2">
                        {selectedPhoto.projectTitle}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white/60">
                    {selectedPhoto.buildingId}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-white/60">
                    {formatPhotoDate(selectedPhoto)}
                  </div>
                  <Link
                    to={`/renovation/${selectedPhoto.projectId}`}
                    className="flex items-center gap-1.5 text-sm font-medium text-moss-primary hover:text-moss-primary/80 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Naar project
                    <ArrowSquareOut size={16} weight="bold" />
                  </Link>
                </div>
              </div>
              {/* Summary input */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <label className="block text-xs text-white/60 mb-1.5">Samenvatting</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editedSummary}
                    onChange={(e) => setEditedSummary(e.target.value)}
                    placeholder="Voeg een samenvatting toe..."
                    className="flex-1 px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-md text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-moss-primary focus:border-transparent"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSavingSummary) {
                        handleSaveSummary();
                      }
                      e.stopPropagation();
                    }}
                  />
                  <button
                    onClick={handleSaveSummary}
                    disabled={isSavingSummary || editedSummary === (selectedPhoto.summary || "")}
                    className="px-3 py-2 bg-moss-primary hover:bg-moss-primary/80 disabled:bg-white/10 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors flex items-center gap-1.5"
                  >
                    {isSavingSummary ? (
                      <SpinnerGap size={16} className="animate-spin" />
                    ) : (
                      <FloppyDisk size={16} weight="bold" />
                    )}
                    Opslaan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
